<template>
  <div class="ov">
    <!-- Resumo -->
    <div class="grid-top">
      <!-- Índice ARC -->
      <div class="card">
        <div class="card-h"><h3>{{ $t('ARC_PANEL.OVERVIEW.INDEX') }}</h3>
          <span v-if="ind.variacao_mes != null" class="pill" :class="ind.variacao_mes >= 0 ? 'ok' : 'bad'">
            {{ ind.variacao_mes >= 0 ? '↑' : '↓' }} {{ Math.abs(ind.variacao_mes) }} {{ $t('ARC_PANEL.OVERVIEW.PTS_MONTH') }}
          </span>
        </div>
        <div class="idx">
          <div class="gauge" role="img" :aria-label="`Índice ${ind.valor}`">
            <svg viewBox="0 0 132 132">
              <circle cx="66" cy="66" r="56" fill="none" stroke="var(--track, #EDF1F5)" stroke-width="11" />
              <circle cx="66" cy="66" r="56" fill="none" stroke="var(--w-500)" stroke-width="11" stroke-linecap="round"
                :stroke-dasharray="`${gaugeLen} ${circ}`" transform="rotate(-90 66 66)" />
            </svg>
            <div class="v"><b class="num">{{ ind.valor }}</b><small>{{ $t('ARC_PANEL.OVERVIEW.OF_100') }}</small></div>
          </div>
          <div class="pbars">
            <div v-for="p in ['A','R','C']" :key="p" class="pbar">
              <div class="row"><span><span class="pl" :class="p">{{ p }}</span>{{ pilarNome(p) }}</span><em class="num">{{ ind.pilares[p] }}</em></div>
              <div class="bar"><i :style="{ width: ind.pilares[p] + '%', background: pilarColor(p) }" /></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Maturidade -->
      <div class="card">
        <div class="card-h"><h3>{{ $t('ARC_PANEL.OVERVIEW.MATURITY') }}</h3>
          <span class="pill info">{{ $t('ARC_PANEL.OVERVIEW.PHASE_OF', { n: journey.fase.ordem }) }} · {{ journey.fase.nome }}</span>
        </div>
        <div class="big">{{ mat.nivel }}</div>
        <div class="muted" v-if="journey.foco">{{ $t('ARC_PANEL.OVERVIEW.FOCUS') }}: {{ journey.foco }}</div>
        <div class="levels">
          <span v-for="(l, i) in niveis" :key="l" :class="segClass(i)" :style="segStyle(i)" />
        </div>
        <div class="lvl-names">
          <span v-for="(l, i) in niveis" :key="l" :class="{ cur: i === mat.indice_nivel }">{{ l }}</span>
        </div>
        <div class="hint">
          <template v-if="mat.proximo">
            {{ $t('ARC_PANEL.OVERVIEW.MATURITY_HINT_1') }} <b class="num">{{ mat.faltam_pts }} {{ $t('ARC_PANEL.OVERVIEW.PTS') }}</b>
            {{ $t('ARC_PANEL.OVERVIEW.MATURITY_HINT_2') }} <b>{{ mat.proximo }}</b>.
            {{ $t('ARC_PANEL.OVERVIEW.MATURITY_HINT_3') }} <b>{{ pilarNome(mat.pilar_mais_fraco) }}</b>.
          </template>
          <template v-else>{{ $t('ARC_PANEL.OVERVIEW.MAX_LEVEL') }}</template>
        </div>
      </div>

      <!-- Valor -->
      <div class="card value">
        <div class="card-h"><h3>{{ $t('ARC_PANEL.OVERVIEW.VALUE') }}</h3>
          <span v-if="!val.vazio" class="pill ok">ROI {{ fmt(val.roi, 1) }}x</span>
        </div>
        <template v-if="val.vazio">
          <p class="empty-val">{{ $t('ARC_PANEL.OVERVIEW.VALUE_EMPTY') }}</p>
        </template>
        <template v-else>
          <div class="value-num num">{{ money(val.acumulado) }}</div>
          <div class="muted">{{ $t('ARC_PANEL.OVERVIEW.VALUE_SUB', { n: val.meses_decorridos, inv: money(val.investido) }) }}</div>
          <div class="stack">
            <i v-for="(c, i) in val.categorias" :key="i" :style="{ width: pct(c.valor, valTotal) + '%', background: pilarColor(c.pilar) }" />
          </div>
          <div class="legend">
            <span v-for="(c, i) in val.categorias" :key="i"><i :style="{ background: pilarColor(c.pilar) }" />{{ c.nome }} · <b class="num">{{ money(c.valor) }}</b></span>
          </div>
          <div class="vsplit num">
            <div><small>{{ $t('ARC_PANEL.OVERVIEW.PAYBACK') }}</small><b>{{ val.payback_dias }} {{ $t('ARC_PANEL.OVERVIEW.DAYS') }}</b></div>
            <div><small>{{ $t('ARC_PANEL.OVERVIEW.MONTHLY_AVG') }}</small><b>{{ money(val.media_mensal) }}</b></div>
            <div><small>{{ $t('ARC_PANEL.OVERVIEW.PROJECTION') }}</small><b>{{ money(val.projecao_12m) }}</b></div>
          </div>
        </template>
      </div>
    </div>

    <!-- Mapa da jornada -->
    <div class="sec">{{ $t('ARC_PANEL.OVERVIEW.MAP') }}</div>
    <div class="card journey">
      <div class="months">
        <button v-for="m in overview.mapa" :key="m.mes" class="month" :class="m.estado" type="button" @click="$emit('goto-month', m.mes)">
          <span v-if="m.estado === 'atual'" class="here">{{ $t('ARC_PANEL.OVERVIEW.YOU_ARE_HERE') }}</span>
          <span class="m">{{ $t('ARC_MGMT.INDICATORS.M') }}{{ m.mes }}<small>{{ m.rotulo_calendario }}</small></span>
          <span class="t"><b v-if="m.marco">{{ m.marco }}</b><template v-else>{{ m.total }} {{ $t('ARC_PANEL.OVERVIEW.DELIVERIES') }}</template></span>
          <span class="mb"><i :style="{ width: pct(m.concluidas, m.total || 1) + '%' }" /></span>
        </button>
      </div>
    </div>

    <!-- Colunas -->
    <div class="grid-2">
      <div class="card">
        <div class="card-h"><h3>{{ $t('ARC_PANEL.OVERVIEW.CURRENT_DELIVERIES') }}</h3></div>
        <div class="prog-h"><div class="bar"><i :style="{ width: pct(curDone, curTotal || 1) + '%', background: 'var(--w-500)' }" /></div>
          <span class="muted num"><b>{{ curDone }}</b> {{ $t('ARC_PANEL.OVERVIEW.OF') }} {{ curTotal }}</span></div>
        <div class="items">
          <div v-for="d in overview.entregas_mes_atual" :key="d.id" class="item">
            <span class="ck" :class="ckClass(d.status)" />
            <div><div class="nm">{{ d.nome }}</div>
              <div class="meta"><span class="pil-txt" :style="{ color: pilarColor(d.pilar) }">{{ pilarNome(d.pilar) }}</span><span class="who" :class="{ cli: d.responsavel === 'cliente' }">{{ d.responsavel === 'cliente' ? $t('ARC_PANEL.OVERVIEW.YOUR_TEAM') : 'Interativa' }}</span></div>
            </div>
            <span class="pill" :class="stPill(d.status)">{{ $t('ARC_MGMT.STATUS.' + d.status) }}</span>
          </div>
        </div>
      </div>

      <div class="stack-col">
        <div class="card">
          <div class="card-h"><h3>{{ $t('ARC_PANEL.OVERVIEW.PENDINGS') }}</h3><span v-if="overview.pendencias.length" class="pill warn">{{ overview.pendencias.length }}</span></div>
          <div v-if="!overview.pendencias.length" class="muted small">{{ $t('ARC_PANEL.OVERVIEW.NO_PENDINGS') }}</div>
          <div v-for="p in overview.pendencias" :key="p.id" class="pend">
            <span class="pic">!</span>
            <div><b>{{ p.titulo }}</b><small>{{ p.contexto }} · {{ $t('ARC_MGMT.PENDINGS.DAYS', { n: p.dias_aberta }) }}</small></div>
            <button v-if="canResolve" class="linkbtn" type="button" @click="resolve(p)">{{ $t('ARC_PANEL.OVERVIEW.RESOLVE') }}</button>
          </div>
        </div>
        <div class="card">
          <div class="card-h"><h3>{{ $t('ARC_PANEL.OVERVIEW.NEXT_MEETINGS') }}</h3></div>
          <div v-for="mtg in overview.proximas_reunioes" :key="mtg.id" class="meet">
            <div class="cal"><small>{{ calMonth(mtg.inicio_em) }}</small><b>{{ calDay(mtg.inicio_em) }}</b></div>
            <div><div class="nm">{{ $t('ARC_MGMT.MEETINGS.TYPES.' + mtg.tipo) }}</div><div class="muted small">{{ mtg.pauta }}</div></div>
          </div>
          <div v-if="overview.proximo_marco" class="meet">
            <div class="cal marco"><small>★</small><b>{{ overview.proximo_marco.mes }}</b></div>
            <div><div class="nm">{{ $t('ARC_PANEL.OVERVIEW.NEXT_MILESTONE') }}: {{ overview.proximo_marco.nome }}</div>
              <div class="muted small">{{ $t('ARC_MGMT.DELIVERABLES.MONTH') }} {{ overview.proximo_marco.mes }}</div></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Resultados -->
    <div class="sec">{{ $t('ARC_PANEL.OVERVIEW.RESULTS') }}</div>
    <div class="kpis">
      <div v-for="k in overview.kpis" :key="k.id" class="card kpi">
        <div class="top-row"><div><div class="kn">{{ k.nome }}</div><div class="src">{{ sourceLabel(k) }}</div></div><span class="pl" :class="k.pilar">{{ k.pilar }}</span></div>
        <div class="cur-v num">{{ kv(k, k.atual) }}</div>
        <div class="delta" :class="deltaClass(k)">{{ deltaText(k) }}</div>
        <div class="ktrack"><i :style="{ width: (k.progresso || 0) * 100 + '%' }" /><span class="goal" /></div>
        <div class="kscale num"><span>{{ $t('ARC_MGMT.INDICATORS.BASE') }} <b>{{ kv(k, k.base_valor) }}</b></span><span>{{ $t('ARC_MGMT.INDICATORS.GOAL') }} <b>{{ kv(k, k.meta_valor) }}</b></span></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ArcAPI from 'dashboard/api/arc';

const PCOL = { A: '#1E88E5', R: '#1F9D63', C: '#7B61FF' };
const PNAME = { A: 'Atendimento', R: 'Receita', C: 'Custo' };

export default {
  props: { overview: { type: Object, required: true } },
  data() { return { niveis: ['Operacional', 'Estruturado', 'Otimizado', 'Preditivo', 'Referência'], circ: 2 * Math.PI * 56 }; },
  computed: {
    ...mapGetters({ currentUser: 'getCurrentUser', access: 'arc/getAccess' }),
    ind() { return this.overview.indice; },
    mat() { return this.overview.indice.maturidade; },
    val() { return this.overview.valor; },
    journey() { return this.overview.journey; },
    gaugeLen() { return this.circ * (this.ind.valor / 100); },
    valTotal() { return (this.val.categorias || []).reduce((s, c) => s + c.valor, 0) || 1; },
    curTotal() { return this.overview.entregas_mes_atual.filter(d => d.status !== 'nao_aplicavel').length; },
    curDone() { return this.overview.entregas_mes_atual.filter(d => ['entregue', 'validado'].includes(d.status)).length; },
    canResolve() {
      return this.access.isConsultant || this.currentUser?.type === 'SuperAdmin' ||
        this.currentUser?.type_role === 'administrator' || this.currentUser?.role === 'administrator';
    },
  },
  methods: {
    pilarColor(p) { return PCOL[p] || 'var(--w-500)'; },
    pilarNome(p) { return PNAME[p] || p; },
    money(v) { return 'R$ ' + Math.round(v || 0).toLocaleString('pt-BR'); },
    fmt(v, d) { return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d }); },
    pct(a, b) { return Math.round((100 * (a || 0)) / (b || 1)); },
    kv(k, v) { if (v === null || v === undefined) return '—'; return this.fmt(v, k.casas_decimais) + (k.unidade || ''); },
    segClass(i) { if (i < this.mat.indice_nivel) return 'on'; if (i === this.mat.indice_nivel) return 'cur'; return ''; },
    segStyle(i) {
      if (i !== this.mat.indice_nivel) return {};
      const faixa = 100 / this.niveis.length;
      const within = this.mat.proximo ? ((100 / this.niveis.length)) : 100;
      return { '--p': within + '%' };
    },
    ckClass(s) { if (['entregue', 'validado'].includes(s)) return 'done'; if (s === 'em_andamento') return 'prog'; if (s === 'aguardando_cliente') return 'wait'; return ''; },
    stPill(s) { return { entregue: 'ok', validado: 'ok', em_andamento: 'info', aguardando_cliente: 'warn' }[s] || 'mute'; },
    sourceLabel(k) {
      if (k.fonte === 'automatico') return 'Automático' + (k.metrica_automatica ? ` · ${k.metrica_automatica}` : '');
      if (k.fonte === 'integracao') return 'Integração' + (k.rotulo_fonte ? ` · ${k.rotulo_fonte}` : '');
      return 'Informado';
    },
    deltaClass(k) {
      if (k.atual == null || k.base_valor == null) return '';
      const impr = k.direcao === 'menor_melhor' ? k.base_valor - k.atual : k.atual - k.base_valor;
      return impr >= 0 ? 'good' : 'bad';
    },
    deltaText(k) {
      if (k.atual == null || k.base_valor == null) return this.$t('ARC_PANEL.OVERVIEW.NO_DATA');
      const arrow = k.direcao === 'menor_melhor' ? '↓' : '↑';
      const pctPath = k.progresso != null ? Math.round(k.progresso * 100) : 0;
      let diff;
      if (k.unidade === '%') diff = `${this.fmt(Math.abs(k.atual - k.base_valor), k.casas_decimais)} p.p.`;
      else if (k.base_valor) diff = `${Math.round((100 * Math.abs(k.atual - k.base_valor)) / k.base_valor)}%`;
      else diff = this.$t('ARC_PANEL.OVERVIEW.SINCE_START');
      return `${arrow} ${diff} ${this.$t('ARC_PANEL.OVERVIEW.VS_START')} · ${pctPath}% ${this.$t('ARC_PANEL.OVERVIEW.OF_GOAL')}`;
    },
    calMonth(iso) { return new Date(iso).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase(); },
    calDay(iso) { return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit' }); },
    async resolve(p) {
      await ArcAPI.resolvePendingClient(p.id);
      this.$emit('refresh');
    },
  },
};
</script>

<style lang="scss" scoped>
.num { font-variant-numeric: tabular-nums; }
.card { background: var(--white); border: 1px solid var(--s-100); border-radius: 12px; box-shadow: 0 1px 2px rgba(0,0,0,.05); padding: var(--space-normal); min-width: 0; }
.card-h { display: flex; justify-content: space-between; align-items: center; gap: var(--space-small); margin-bottom: var(--space-small);
  h3 { margin: 0; font-size: var(--font-size-default); font-weight: var(--font-weight-medium); color: var(--s-900); } }
.grid-top { display: grid; grid-template-columns: 1.25fr 1fr 1.15fr; gap: var(--space-small); }
.grid-2 { display: grid; grid-template-columns: 1.4fr 1fr; gap: var(--space-small); margin-top: var(--space-small); }
.stack-col { display: flex; flex-direction: column; gap: var(--space-small); }
@media (max-width: 1100px) { .grid-top { grid-template-columns: 1fr 1fr; } .grid-top .value { grid-column: 1 / -1; } .grid-2 { grid-template-columns: 1fr; } }
@media (max-width: 700px) { .grid-top { grid-template-columns: 1fr; } .kpis { grid-template-columns: 1fr !important; } }

.pill { display: inline-flex; align-items: center; gap: 5px; border-radius: 20px; padding: 3px 10px; font-size: var(--font-size-mini); font-weight: var(--font-weight-medium); white-space: nowrap;
  &.ok { background: var(--g-100); color: var(--g-800); } &.warn { background: var(--y-100); color: var(--y-700); }
  &.bad { background: var(--r-100); color: var(--r-600); } &.info { background: var(--w-50); color: var(--w-700); } &.mute { background: var(--s-75); color: var(--s-500); } }

.idx { display: grid; grid-template-columns: 132px 1fr; gap: var(--space-normal); align-items: center; }
.gauge { position: relative; width: 132px; height: 132px; svg { width: 100%; height: 100%; } .v { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
  b { font-size: 34px; font-weight: 800; color: var(--s-900); line-height: 1; } small { display: block; font-size: 10.5px; color: var(--s-400); font-weight: var(--font-weight-medium); } }
.pbars { display: flex; flex-direction: column; gap: 11px; }
.pbar .row { display: flex; justify-content: space-between; font-size: var(--font-size-small); font-weight: var(--font-weight-medium); color: var(--s-800); margin-bottom: 5px; em { font-style: normal; color: var(--s-500); } }
.pl { display: inline-grid; place-items: center; width: 18px; height: 18px; border-radius: 5px; font-size: 10.5px; font-weight: 800; margin-right: 6px;
  &.A { background: #E6F1FC; color: #1E88E5; } &.R { background: #E7F6EE; color: #1F9D63; } &.C { background: #EFECFF; color: #7B61FF; } }
.bar { height: 7px; border-radius: 6px; background: var(--s-75); overflow: hidden; i { display: block; height: 100%; border-radius: 6px; } }

.big { font-size: 22px; font-weight: var(--font-weight-bold); color: var(--s-900); }
.muted { font-size: var(--font-size-small); color: var(--s-500); } .muted.small { font-size: var(--font-size-mini); }
.levels { display: flex; gap: 4px; margin: 12px 0 10px; span { flex: 1; height: 8px; border-radius: 4px; background: var(--s-75); }
  span.on { background: var(--w-500); } span.cur { background: linear-gradient(90deg, var(--w-500) var(--p), var(--s-75) var(--p)); } }
.lvl-names { display: flex; justify-content: space-between; font-size: 10px; color: var(--s-400); font-weight: var(--font-weight-medium); gap: 4px; .cur { color: var(--w-700); } }
.hint { margin-top: 12px; padding: 10px 12px; border-radius: 10px; background: var(--s-25); border: 1px dashed var(--s-200); font-size: var(--font-size-small); color: var(--s-700); b { color: var(--s-900); } }

.value-num { font-size: 30px; font-weight: 800; color: var(--g-800); line-height: 1.1; }
.empty-val { color: var(--s-500); font-size: var(--font-size-small); }
.stack { display: flex; height: 8px; border-radius: 5px; overflow: hidden; margin-top: 10px; background: var(--s-75); i { display: block; } }
.legend { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 7px; font-size: var(--font-size-mini); color: var(--s-500); i { display: inline-block; width: 8px; height: 8px; border-radius: 2px; margin-right: 5px; } }
.vsplit { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--s-100); div small { display: block; font-size: 10.5px; color: var(--s-400); font-weight: var(--font-weight-medium); } div b { font-size: 15px; color: var(--s-900); } }

.sec { font-size: 11px; font-weight: var(--font-weight-bold); letter-spacing: .6px; color: var(--s-400); text-transform: uppercase; margin: var(--space-normal) 0 var(--space-small); }
.journey .months { display: grid; grid-template-columns: repeat(12, 1fr); gap: 6px; padding-top: 14px; }
@media (max-width: 900px) { .journey { overflow-x: auto; padding-top: 6px; } .journey .months { min-width: 820px; } }
.month { border: 1px solid var(--s-100); background: var(--white); border-radius: 10px; padding: 9px 8px 8px; text-align: left; display: flex; flex-direction: column; gap: 6px; min-height: 92px; position: relative; cursor: pointer;
  &:hover { border-color: var(--w-500); } .m { display: flex; justify-content: space-between; align-items: center; font-size: 11px; font-weight: var(--font-weight-bold); color: var(--s-900); small { font-weight: var(--font-weight-normal); color: var(--s-400); font-size: 10px; } }
  .t { font-size: 10.5px; color: var(--s-500); line-height: 1.3; flex: 1; b { color: var(--s-900); } } .mb { height: 4px; border-radius: 3px; background: var(--s-75); overflow: hidden; i { display: block; height: 100%; background: var(--g-500); } }
  &.atual { border: 1.5px solid var(--w-500); box-shadow: 0 0 0 3px var(--w-50); .mb i { background: var(--w-500); } } &.futuro { background: var(--s-25); .m { color: var(--s-400); } } }
.here { position: absolute; top: -9px; left: 50%; transform: translateX(-50%); background: var(--w-500); color: #fff; font-size: 9px; font-weight: 800; padding: 2px 7px; border-radius: 8px; white-space: nowrap; }

.prog-h { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; .bar { flex: 1; } }
.items { display: flex; flex-direction: column; }
.item { display: grid; grid-template-columns: 22px 1fr auto; gap: 10px; align-items: center; padding: 9px 0; border-top: 1px solid var(--s-75); &:first-child { border-top: 0; }
  .nm { font-size: var(--font-size-small); color: var(--s-900); font-weight: var(--font-weight-medium); } .meta { font-size: var(--font-size-mini); color: var(--s-400); margin-top: 3px; display: flex; align-items: center; gap: 8px; } .pil-txt { font-weight: var(--font-weight-medium); } .who { background: var(--s-75); color: var(--s-600); padding: 1px 8px; border-radius: 9999px; font-weight: var(--font-weight-medium); } .who.cli { background: var(--y-100); color: var(--y-700); } }
.ck { width: 18px; height: 18px; border-radius: 6px; border: 1.6px solid var(--s-200);
  &.done { background: var(--g-500); border-color: var(--g-500); } &.prog { border-color: var(--w-500); background: conic-gradient(var(--w-500) 0 50%, transparent 50%); } &.wait { border-color: var(--y-500); border-style: dashed; } }
.pend { display: grid; grid-template-columns: auto 1fr auto; gap: 10px; align-items: center; padding: 10px 12px; border-radius: 10px; background: var(--y-50); margin-bottom: 8px;
  b { display: block; font-size: var(--font-size-small); color: var(--s-900); } small { font-size: var(--font-size-mini); color: var(--y-700); } .pic { width: 26px; height: 26px; border-radius: 8px; background: var(--white); display: grid; place-items: center; color: var(--y-700); font-weight: 800; } }
.linkbtn { border: 0; background: none; color: var(--w-600); font-size: var(--font-size-mini); cursor: pointer; }
.meet { display: grid; grid-template-columns: 48px 1fr; gap: 12px; align-items: center; padding: 10px 0; border-top: 1px solid var(--s-75); &:first-of-type { border-top: 0; }
  .nm { font-size: var(--font-size-small); font-weight: var(--font-weight-medium); color: var(--s-900); } }
.cal { border: 1px solid var(--s-100); border-radius: 9px; overflow: hidden; text-align: center; small { display: block; background: var(--w-500); color: #fff; font-size: 9px; font-weight: var(--font-weight-bold); padding: 2px 0; } b { display: block; font-size: 17px; color: var(--s-900); padding: 3px 0 4px; } &.marco small { background: var(--y-500); } }

.kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--space-small); }
@media (max-width: 1100px) { .kpis { grid-template-columns: 1fr 1fr; } }
.kpi .top-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; }
.kpi .kn { font-size: var(--font-size-small); color: var(--s-900); font-weight: var(--font-weight-medium); } .kpi .src { font-size: 10.5px; color: var(--s-400); margin-top: 2px; }
.kpi .cur-v { font-size: 24px; font-weight: var(--font-weight-bold); color: var(--s-900); margin: 10px 0 2px; }
.kpi .delta { font-size: 11.5px; font-weight: var(--font-weight-medium); &.good { color: var(--g-700); } &.bad { color: var(--r-600); } }
.ktrack { position: relative; height: 8px; border-radius: 5px; background: var(--s-75); margin: 14px 0 6px; i { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 5px; background: var(--w-500); } .goal { position: absolute; right: -1px; top: -4px; width: 2px; height: 16px; background: var(--s-900); border-radius: 1px; } }
.kscale { display: flex; justify-content: space-between; font-size: 10.5px; color: var(--s-400); b { color: var(--s-700); } }
</style>
